/**
 * Device detection utility functions
 */

/**
 * Check if the current device is a mobile or tablet
 * @returns {boolean} - True if device is mobile/tablet
 */
export function isMobileDevice() {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    
    // Check for mobile/tablet devices
    const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;
    const isMobile = mobileRegex.test(userAgent.toLowerCase());
    
    // Additional check for screen size
    const isSmallScreen = window.innerWidth <= 1024;
    
    return isMobile || isSmallScreen;
}

/**
 * Get the device operating system
 * @returns {string} - 'ios', 'android', or 'other'
 */
export function getDeviceOS() {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    
    if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
        return 'ios';
    } else if (/android/i.test(userAgent)) {
        return 'android';
    }
    
    // Default to iOS for other devices (fallback)
    return 'ios';
}

/**
 * Check if the device is iOS
 * @returns {boolean} - True if device is iOS
 */
export function isIOS() {
    return getDeviceOS() === 'ios';
}

/**
 * Check if the device is Android
 * @returns {boolean} - True if device is Android
 */
export function isAndroid() {
    return getDeviceOS() === 'android';
}

/**
 * Get the appropriate app store link based on device OS
 * @param {string} iosAppId - iOS App Store ID
 * @param {string} androidPackageName - Android package name
 * @returns {string} - App store URL
 */
export function getAppStoreLink(iosAppId, androidPackageName) {
    const deviceOS = getDeviceOS();
    
    if (deviceOS === 'ios') {
        return `https://apps.apple.com/app/id${iosAppId}`;
    } else {
        return `https://play.google.com/store/apps/details?id=${androidPackageName}`;
    }
}

/**
 * Check if the device is a tablet
 * @returns {boolean} - True if device is a tablet
 */
export function isTablet() {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    
    // Check for tablet-specific user agents
    const tabletRegex = /ipad|android(?=.*\bMobile\b)(?=.*\bSafari\b)/i;
    const isTabletUA = tabletRegex.test(userAgent);
    
    // Check screen size for tablets
    const isTabletScreen = window.innerWidth >= 768 && window.innerWidth <= 1024;
    
    return isTabletUA || isTabletScreen;
}

/**
 * Check if the device is a phone
 * @returns {boolean} - True if device is a phone
 */
export function isPhone() {
    return isMobileDevice() && !isTablet();
}

/**
 * Get device screen size category
 * @returns {string} - 'mobile', 'tablet', or 'desktop'
 */
export function getScreenSize() {
    if (window.innerWidth < 768) {
        return 'mobile';
    } else if (window.innerWidth <= 1024) {
        return 'tablet';
    } else {
        return 'desktop';
    }
}

/**
 * Check if the device supports touch
 * @returns {boolean} - True if device supports touch
 */
export function isTouchDevice() {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
}

/**
 * Get device information object
 * @returns {object} - Device information
 */
export function getDeviceInfo() {
    return {
        isMobile: isMobileDevice(),
        isTablet: isTablet(),
        isPhone: isPhone(),
        isIOS: isIOS(),
        isAndroid: isAndroid(),
        isTouch: isTouchDevice(),
        screenSize: getScreenSize(),
        userAgent: navigator.userAgent,
        screenWidth: window.innerWidth,
        screenHeight: window.innerHeight
    };
} 